import { ArrowRight, Bot, FileSearch, ScanText, ShieldCheck, Target, UserRound } from "lucide-react"
import type { LucideIcon } from "lucide-react"

/**
 * Why PRAXA, in one screen: most job tools stop at finding the posting, and
 * the application then meets software before it meets a person.
 *
 * Every claim here is something the code does - the tailoring prompt
 * (`tools/documents.py`), the fit score's matched / missing lists
 * (`tools/fit.py`), the single-column Word export (`documents/word.py`) and
 * the reviewing agent. No statistics about how many CVs an ATS rejects: the
 * figures that circulate are unsourced, and a page arguing for honesty
 * should not open with an invented number.
 */
export function WhyPraxa() {
  return (
    <section id="why" className="mx-auto max-w-6xl scroll-mt-20 px-6 pb-20">
      <div className="mb-10 text-center">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-primary">Why PRAXA</p>
        <h2 className="mt-2 font-display text-3xl font-bold tracking-tight">
          Finding the job is the easy part
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
          Most job tools stop at the posting. But before a person reads your application, software
          usually does: an applicant tracking system (ATS) reads the file and compares it with the
          posting&apos;s words. Experience you really have can be missed just because the CV words it
          differently, or because the file layout confuses the parser. PRAXA is built for that step
          - getting what you actually know in front of a human.
        </p>
      </div>

      <Journey />

      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Reason
          icon={Target}
          title="Speaks the posting's language"
          body="Your CV is re-worded with the posting's own terms - wherever your experience supports them - so the match the software looks for is there."
        />
        <Reason
          icon={FileSearch}
          title="Shows the gaps before you apply"
          body="Every posting is checked against your CV: what matches, what is missing, and whether it is worth applying. No surprises after you press send."
        />
        <Reason
          icon={ScanText}
          title="A file the machine can read"
          body="One column, real headings, no tables or text boxes - the layouts parsers are known to scramble are simply never produced."
        />
        <Reason
          icon={ShieldCheck}
          title="Still true when a person reads it"
          body="Nothing is invented to beat the filter. A second agent checks every claim against your CV, because the interview will."
        />
      </div>
    </section>
  )
}

/** Your experience -> the software -> a person, with what PRAXA does at each step. */
function Journey() {
  return (
    <ol className="grid items-stretch gap-3 md:grid-cols-[1fr_auto_1fr_auto_1fr]">
      <Stage
        icon={UserRound}
        step="Your experience"
        body="Everything you have done - in your words, which are not always the posting's."
      />
      <Arrow />
      <Stage
        icon={Bot}
        step="The software"
        body="Reads the file and looks for the posting's terms. Missed wording or a broken layout can stop you here."
        highlight
      />
      <Arrow />
      <Stage
        icon={UserRound}
        step="A person"
        body="A recruiter reads what got through - and an interviewer asks about every line."
      />
    </ol>
  )
}

function Stage({
  icon: Icon,
  step,
  body,
  highlight = false,
}: {
  icon: LucideIcon
  step: string
  body: string
  highlight?: boolean
}) {
  return (
    <li
      className={
        highlight
          ? "rounded-2xl border border-primary/40 bg-primary/5 p-5"
          : "rounded-2xl border bg-card/60 p-5 backdrop-blur"
      }
    >
      <Icon className={highlight ? "size-5 text-primary" : "size-5 text-muted-foreground"} aria-hidden />
      <p className="mt-3 font-semibold">{step}</p>
      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{body}</p>
      {highlight ? (
        <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-primary">
          Where PRAXA helps most
        </p>
      ) : null}
    </li>
  )
}

function Arrow() {
  return (
    <li aria-hidden className="hidden items-center justify-center text-muted-foreground md:flex">
      <ArrowRight className="size-5" />
    </li>
  )
}

function Reason({ icon: Icon, title, body }: { icon: LucideIcon; title: string; body: string }) {
  return (
    <div className="rounded-2xl border bg-card/60 p-5 backdrop-blur">
      <span className="grid size-9 place-items-center rounded-xl bg-primary/10 text-primary">
        <Icon className="size-4" aria-hidden />
      </span>
      <h3 className="mt-3 font-semibold">{title}</h3>
      <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{body}</p>
    </div>
  )
}
